import { Component, computed, inject, signal, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { IdReferenceDto, UserItemDto, UserStatisticsFilterDto, WeekYear } from '@artemis-software/wr-api';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import { closeOpenEnd } from '@/utils/filter-range-utils';
import { DateRangeObject } from '@models/date-range';
import { StatisticsService } from '@services/statistics.service';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { NbDateService } from '@nebular/theme';
import { eachWeekOfInterval, endOfDay, format, startOfDay } from 'date-fns';
import { loader } from '@/utils/signals/loader';
import { downloadDelimitedCsv, excelTextGuard, germanExcelNumber, quoteCsvField } from '@/utils/csv-download';
import { Select } from '@ngxs/store';
import { UserState } from '@/store/user/user.state';
import { Observable } from 'rxjs';

interface UserStatistics {
  squareMetersDataset: number[];
  inspectionsDataset: number[];
  entriesDataset: number[];
  buildingSectionsDataset: number[];
}

interface ChartCardConfig {
  title: string;
  datasetLabel: string;
  csvName: string;
  csvHeader: string;
  color: string;
  stack: string;
  data: number[];
}

interface WeekStat {
  week: WeekYear;
  info: number;
}

function fillWeeklyDataset(stats: WeekStat[], labelIndexMap: Map<string, number>, target: number[]): void {
  stats.forEach(stat => {
    const weekIndex = labelIndexMap.get(`${stat.week.week}/${stat.week.year}`);
    if (weekIndex !== undefined) {
      target[weekIndex] = stat.info;
    }
  });
}

function toEndOfDayIso(date: Date | undefined): string | undefined {
  return date ? endOfDay(date).toISOString() : undefined;
}

function toStartOfDayIso(date: Date | undefined): string | undefined {
  return date ? startOfDay(date).toISOString() : undefined;
}

@Component({
  standalone: false,
  selector: 'wr-employee-statistics-page',
  templateUrl: './employee-statistics-page.component.html',
  styleUrls: ['./employee-statistics-page.component.scss']
})
export class EmployeeStatisticsPageComponent {

  isLoading = loader();

  readonly filter: WritableSignal<UserStatisticsFilterDto> = signal({
    userIds: [],
  });

  readonly statistics = asyncComputed(
    () => {
      const filter = this.filter();
      return this.statisticsService.getUserStatistics({
        ...filter,
        userIds: filter.userIds ?? [],
      });
    },
    undefined,
    this.isLoading
  );

  readonly datasets: WritableSignal<UserStatistics> = asyncComputed(
    () => {
      const statistics = this.statistics();
      const labels = this.labels();
      if (!statistics || !labels) {
        return {
          squareMetersDataset: [],
          inspectionsDataset: [],
          entriesDataset: [],
          buildingSectionsDataset: [],
        };
      }

      const labelIndexMap = new Map<string, number>();
      labels.forEach((label, index) => {
        labelIndexMap.set(label, index);
      });

      const squareMetersDataset: number[] = new Array(labels.length).fill(0);
      const inspectionsDataset: number[] = new Array(labels.length).fill(0);
      const entriesDataset: number[] = new Array(labels.length).fill(0);
      const buildingSectionsDataset: number[] = new Array(labels.length).fill(0);

      fillWeeklyDataset(statistics.squareMetersPerWeek, labelIndexMap, squareMetersDataset);
      fillWeeklyDataset(statistics.inspectionsPerWeek, labelIndexMap, inspectionsDataset);
      fillWeeklyDataset(statistics.entriesPerWeek, labelIndexMap, entriesDataset);
      fillWeeklyDataset(statistics.buildingSectionsPerWeek, labelIndexMap, buildingSectionsDataset);

      return {
        squareMetersDataset,
        inspectionsDataset,
        entriesDataset,
        buildingSectionsDataset,
      };
    },
    {
      squareMetersDataset: [],
      inspectionsDataset: [],
      entriesDataset: [],
      buildingSectionsDataset: [],
    }
  );

  readonly labels = asyncComputed(
    () => {
      const filter = this.filter();
      if (!filter.from || !filter.to) {
        return [];
      }

      const startDate = filter.from;
      const endDate = filter.to;

      // weekStartsOn: 1 = Monday, matching ISO week definition used by the backend.
      const weeks = eachWeekOfInterval({ start: startDate, end: endDate }, { weekStartsOn: 1 });
      return weeks.map(week => `${format(week, 'I')}/${format(week, 'RRRR')}`);
    },
    []
  );

  @Select(UserState.users)
  users$!: Observable<UserItemDto[]>;

  readonly users = toSignal(this.users$, { initialValue: [] as UserItemDto[] });

  readonly technicianSummary = computed(() => {
    const userIds = this.filter().userIds ?? [];
    const users = this.users();

    if (userIds.length === 0 || (users.length > 0 && userIds.length === users.length)) {
      return 'Alle Techniker';
    }

    const selectedIds = new Set(userIds);
    return users
      .filter(user => selectedIds.has(user.id))
      .map(user => user.fullName)
      .sort((a, b) => a.localeCompare(b))
      .join(', ');
  });

  readonly timeframeSummary = computed(() => {
    const { from, to } = this.filter();
    if (!from || !to) {
      return '';
    }
    return `${format(new Date(from), 'dd.MM.yyyy')} – ${format(new Date(to), 'dd.MM.yyyy')}`;
  });

  readonly chartCards = computed((): ChartCardConfig[] => {
    const datasets = this.datasets();
    return [
      {
        title: 'Einträge pro Woche',
        datasetLabel: 'Einträge pro Woche',
        csvName: 'eintraege-pro-woche',
        csvHeader: 'Einträge',
        color: '#4c84ff',
        stack: 'entries',
        data: datasets.entriesDataset,
      },
      {
        title: 'Gebäude pro Woche',
        datasetLabel: 'Gebäude pro Woche',
        csvName: 'gebaeude-pro-woche',
        csvHeader: 'Gebäude',
        color: '#3dc8ab',
        stack: 'inspections',
        data: datasets.inspectionsDataset,
      },
      {
        title: 'Eingänge pro Woche',
        datasetLabel: 'Eingänge pro Woche',
        csvName: 'eingaenge-pro-woche',
        csvHeader: 'Eingänge',
        color: '#f4a259',
        stack: 'buildingSections',
        data: datasets.buildingSectionsDataset,
      },
      {
        title: 'Fläche pro Woche',
        datasetLabel: 'Fläche pro Woche - m²',
        csvName: 'flaeche-pro-woche',
        csvHeader: 'Fläche - m²',
        color: '#ff6361',
        stack: 'squareMeters',
        data: datasets.squareMetersDataset,
      },
    ];
  });

  form!: FormGroup;

  private readonly formBuilder = inject(FormBuilder);
  private readonly dateService = inject(NbDateService<Date>);
  private readonly statisticsService = inject(StatisticsService);

  constructor() {
    this.initFilter();
  }

  initFilter() {
    const filter = localStorage.getItem('userStatisticsFilter');
    this.initFilterForm();

    if (filter) {
      const filterValue = JSON.parse(filter);
      const selectedRange: DateRangeObject | undefined = filterValue.selectedRange ?? undefined;
      const queryRange = closeOpenEnd(selectedRange);

      const userIds = filterValue.technicianReferences
        ? filterValue.technicianReferences.map((technicianReference: IdReferenceDto) => technicianReference.value)
        : [];

      this.filter.set({
        ...this.filter(),
        userIds,
        from: queryRange ? toStartOfDayIso(queryRange.start) : this.filter().from,
        to: queryRange ? toEndOfDayIso(queryRange.end) : this.filter().to,
      });

      this.addSpecialFields(selectedRange, filterValue.technicianReferences);
    } else {
      const formValue = this.form.value;
      this.filter.set({
        ...this.filter(),
        from: formValue.selectedRange?.start?.toISOString(),
        to: toEndOfDayIso(formValue.selectedRange?.end),
      });
    }
  }

  initFilterForm(): void {
    const startOfMonth = this.dateService.getMonthStart(this.dateService.today());
    const endOfMonth = this.dateService.getMonthEnd(this.dateService.addMonth(startOfMonth, 1));

    this.form = this.formBuilder.group({
      technicianReferences: this.formBuilder.control(null),
      selectedRange: new FormControl({
        start: startOfMonth,
        end: endOfMonth,
      }),
    });
  }

  addSpecialFields(selectedRange: DateRangeObject | undefined, technicianReferences: IdReferenceDto[]): void {
    this.form.patchValue({
      'selectedRange': selectedRange,
      'technicianReferences': technicianReferences,
    });
  }

  triggerFilter(): void {
    const formValue = { ...this.form.value, selectedRange: closeOpenEnd(this.form.value.selectedRange) };

    this.filter.set({
      ...this.filter(),
      userIds: formValue.technicianReferences ? [...formValue.technicianReferences.map(
        (technicianReference: IdReferenceDto) => technicianReference.value
      )] : [],
      from: toStartOfDayIso(formValue.selectedRange?.start),
      to: toEndOfDayIso(formValue.selectedRange?.end),
    });

    localStorage.setItem('userStatisticsFilter', JSON.stringify(this.form.value));
  }

  exportCsv(filename: string, valueHeader: string, dataset: number[]): void {
    const labels = this.labels() ?? [];
    const rows: string[][] = [[quoteCsvField('Woche'), quoteCsvField(valueHeader)]];
    labels.forEach((label, index) => {
      const value = dataset[index] ?? 0;
      rows.push([quoteCsvField(excelTextGuard(label)), germanExcelNumber(value)]);
    });

    downloadDelimitedCsv(rows, `${filename}.csv`);
  }

  printPage(): void {
    window.print();
  }
}
